import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchExtendedPatientSummary,
  // getAuthHeaders,
  buildApiEndpoint,
  authenticatedRequest,
} from "../services/api-config";
import { usePatientStore } from "../stores/patientStore";
import type { PatientsListItem } from "../services/patients";
import type {
  ExtendedPatientSummary,
  D1NAMOPatientProfile,
} from "../types/api";

// Enhanced types for the new API response
interface EnhancedPatientsListResponse {
  success: boolean;
  patients: (PatientsListItem & {
    glucoseReadings?: number;
    ecgReadings?: number;
    insulinMeasurements?: number;
    mealImages?: number;
    dataCompletenessScore?: number;
    cohort?: "diabetes" | "healthy";
  })[];
  metadata?: {
    recordCount: number;
    queryTime: number;
    performanceMetrics?: {
      queryExecutionTime: number;
      dataProcessingTime: number;
      totalResponseTime: number;
    };
  };
}

interface PatientSearchRequest {
  query?: string;
  cohort?: "diabetes" | "healthy" | "all";
  gender?: "Male" | "Female" | "Other";
  minAge?: number;
  maxAge?: number;
  hasGlucoseData?: boolean;
  hasEcgData?: boolean;
  hasInsulinData?: boolean;
  hasMealImages?: boolean;
  minDataCompleteness?: number;
  page?: number;
  limit?: number;
}

interface PatientSearchResponse {
  patients: PatientsListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: PatientSearchRequest;
}

export function usePatients(options?: {
  page?: number;
  limit?: number;
  cohort?: "diabetes" | "healthy" | "all";
  gender?: "Male" | "Female" | "Other";
  minAge?: number;
  maxAge?: number;
  hasGlucoseData?: boolean;
  hasEcgData?: boolean;
  hasInsulinData?: boolean;
  hasMealImages?: boolean;
  minDataCompleteness?: number;
  query?: string;
}) {
  const setPatients = usePatientStore((state) => state.setPatients);
  const setPagination = usePatientStore((state) => state.setPagination);
  const patients = usePatientStore((state) => state.patients);

  const queryParams = new URLSearchParams();
  if (options?.page) queryParams.append("page", options.page.toString());
  if (options?.limit) queryParams.append("limit", options.limit.toString());
  if (options?.cohort && options.cohort !== "all")
    queryParams.append("cohort", options.cohort);
  if (options?.gender) queryParams.append("gender", options.gender);
  if (options?.minAge) queryParams.append("minAge", options.minAge.toString());
  if (options?.maxAge) queryParams.append("maxAge", options.maxAge.toString());
  if (options?.hasGlucoseData !== undefined)
    queryParams.append("hasGlucoseData", options.hasGlucoseData.toString());
  if (options?.hasEcgData !== undefined)
    queryParams.append("hasEcgData", options.hasEcgData.toString());
  if (options?.hasInsulinData !== undefined)
    queryParams.append("hasInsulinData", options.hasInsulinData.toString());
  if (options?.hasMealImages !== undefined)
    queryParams.append("hasMealImages", options.hasMealImages.toString());
  if (options?.minDataCompleteness)
    queryParams.append(
      "minDataCompleteness",
      options.minDataCompleteness.toString(),
    );
  if (options?.query) queryParams.append("query", options.query);

  const endpoint = buildApiEndpoint(
    "/patients",
    options ? Object.fromEntries(queryParams.entries()) : undefined,
  );

  return useQuery({
    queryKey: ["patients", options],
    queryFn: async ({ signal }) => {
      const response = await authenticatedRequest(endpoint, {
        method: "GET",
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch patients: ${response.statusText}`);
      }

      const data: EnhancedPatientsListResponse = await response.json();

      // Update store with patients data
      if (data.patients) {
        setPatients(data.patients);

        // Update patient data counts in store
        const patientCounts = data.patients.map((patient) => ({
          patientId: patient.patientId,
          counts: {
            glucoseReadings: patient.glucoseReadings || 0,
            ecgReadings: patient.ecgReadings || 0,
            insulinMeasurements: patient.insulinMeasurements || 0,
            mealImages: patient.mealImages || 0,
            dataCompletenessScore: patient.dataCompletenessScore || 0,
          },
        }));

        // Use batch update for better performance
        const batchUpdateCounts =
          usePatientStore.getState().batchUpdatePatientCounts;
        if (batchUpdateCounts) {
          batchUpdateCounts(patientCounts);
        }
      }

      // Update pagination if metadata is available
      if (data.metadata?.recordCount !== undefined) {
        const currentPage = options?.page || 1;
        const limit = options?.limit || 20;
        const totalPages = Math.ceil(data.metadata.recordCount / limit);

        setPagination({
          page: currentPage,
          limit,
          total: data.metadata.recordCount,
          totalPages,
          hasNext: currentPage < totalPages,
          hasPrev: currentPage > 1,
        });
      }

      return data;
    },
    initialData:
      patients.length > 0 && !options ? { success: true, patients } : undefined,
    staleTime: 2 * 60 * 1000,
    select: (data) => {
      // Sort patients by data completeness if no specific ordering is requested
      if (!options?.query && !options?.cohort && !options?.gender) {
        const sortedPatients = [...(data.patients || [])].sort((a, b) => {
          const aScore =
            (a.glucoseReadings || 0) +
            (a.ecgReadings || 0) +
            (a.insulinMeasurements || 0) +
            (a.mealImages || 0);
          const bScore =
            (b.glucoseReadings || 0) +
            (b.ecgReadings || 0) +
            (b.insulinMeasurements || 0) +
            (b.mealImages || 0);
          return bScore - aScore;
        });
        return { ...data, patients: sortedPatients };
      }
      return data;
    },
  });
}

/**
 * Hook for fetching a single patient with extended D1NAMO profile information
 */
export function usePatient(
  patientId?: string,
  options?: { includeExtended?: boolean; includeQuality?: boolean },
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      "patient",
      patientId,
      options?.includeExtended,
      options?.includeQuality,
    ],
    queryFn: async ({ signal }) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      const endpoint = buildApiEndpoint(
        `/patients/${patientId}`,
        options?.includeQuality ? { includeQuality: "true" } : undefined,
      );
      const response = await authenticatedRequest(endpoint, {
        method: "GET",
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch patient: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        success: boolean;
        patient: PatientsListItem & {
          glucoseReadings?: number;
          ecgReadings?: number;
          insulinMeasurements?: number;
          mealImages?: number;
          earliestReading?: string;
          latestReading?: string;
          dataCompletenessScore?: number;
          dataQuality?: {
            glucoseQuality: number;
            ecgQuality: number;
            insulinQuality: number;
            mealImageQuality: number;
            overallQuality: number;
            lastUpdated: string;
          };
          d1namoProfile?: D1NAMOPatientProfile;
          latestReadings?: {
            glucose?: { timestamp: string; value: number; unit: string };
            ecg?: { timestamp: string; heartRate: number; quality: string };
            insulin?: { timestamp: string; units: number; type: string };
            mealImage?: { timestamp: string; description?: string };
          };
        };
        metadata?: {
          recordCount: number;
          queryTime: number;
          performanceMetrics?: {
            queryExecutionTime: number;
            dataProcessingTime: number;
            totalResponseTime: number;
          };
        };
      };

      return data.patient;
    },
    enabled: Boolean(patientId),
    staleTime: 5 * 60 * 1000,
  });

  const extendedQuery = useQuery({
    queryKey: ["patient-extended", patientId],
    queryFn: async () => {
      if (!patientId || !options?.includeExtended) {
        return null;
      }

      const response = await fetchExtendedPatientSummary(patientId);
      return response;
    },
    enabled: Boolean(patientId) && Boolean(options?.includeExtended),
    staleTime: 10 * 60 * 1000,
  });

  /**
   * Mutation for updating patient information
   */
  const updatePatient = useMutation({
    mutationFn: async (
      patientData: Partial<PatientsListItem & ExtendedPatientSummary>,
    ) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      const response = await authenticatedRequest(
        buildApiEndpoint(`/patients/${patientId}`),
        {
          method: "PUT",
          body: patientData,
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to update patient: ${response.statusText}`);
      }

      return response.json() as Promise<
        PatientsListItem & ExtendedPatientSummary
      >;
    },
    onMutate: async (updatedData) => {
      await queryClient.cancelQueries({ queryKey: ["patient", patientId] });
      await queryClient.cancelQueries({ queryKey: ["patients"] });

      const previousPatient = queryClient.getQueryData(["patient", patientId]);
      const previousPatients = queryClient.getQueryData<PatientsListItem[]>([
        "patients",
      ]);

      // Optimistically update patient in cache
      queryClient.setQueryData(["patient", patientId], (old: any) =>
        old ? { ...old, ...updatedData } : null,
      );

      // Update patient in list if present
      queryClient.setQueryData(
        ["patients"],
        (old: PatientsListItem[] | undefined) =>
          old?.map((patient) =>
            patient.patientId === patientId
              ? { ...patient, ...updatedData }
              : patient,
          ),
      );

      return { previousPatient, previousPatients };
    },
    onError: (_err, _updatedData, context) => {
      if (context?.previousPatient) {
        queryClient.setQueryData(
          ["patient", patientId],
          context.previousPatient,
        );
      }
      if (context?.previousPatients) {
        queryClient.setQueryData(["patients"], context.previousPatients);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });

  /**
   * Mutation for updating D1NAMO patient profile
   */
  const updatePatientProfile = useMutation({
    mutationFn: async (profileData: Partial<D1NAMOPatientProfile>) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      const response = await authenticatedRequest(
        buildApiEndpoint(`/patient-profiles/${patientId}`),
        {
          method: "PUT",
          body: profileData,
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update patient profile: ${response.statusText}`,
        );
      }

      return response.json() as Promise<D1NAMOPatientProfile>;
    },
    onMutate: async (profileData) => {
      await queryClient.cancelQueries({
        queryKey: ["patient-profile", patientId],
      });

      const previousProfile = queryClient.getQueryData([
        "patient-profile",
        patientId,
      ]);

      queryClient.setQueryData(["patient-profile", patientId], (old: any) =>
        old ? { ...old, ...profileData } : null,
      );

      return { previousProfile };
    },
    onError: (_err, _profileData, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(
          ["patient-profile", patientId],
          context.previousProfile,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-profile", patientId],
      });
    },
  });

  return {
    // Basic patient data
    patient: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,

    // Extended patient data
    extendedPatient: extendedQuery.data,
    isLoadingExtended: extendedQuery.isLoading,
    isErrorExtended: extendedQuery.isError,
    errorExtended: extendedQuery.error,

    // Mutations
    updatePatient: updatePatient.mutate,
    updatePatientProfile: updatePatientProfile.mutate,

    // Mutation states
    isUpdating: updatePatient.isPending,
    isUpdatingProfile: updatePatientProfile.isPending,

    // Utilities
    refetch: () => {
      query.refetch();
      if (options?.includeExtended) {
        extendedQuery.refetch();
      }
    },
  };
}

/**
 * Hook for fetching D1NAMO patient profile
 */
export function usePatientProfile(patientId?: string) {
  return useQuery({
    queryKey: ["patient-profile", patientId],
    queryFn: async ({ signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for patient profile");
      }

      const response = await authenticatedRequest(
        buildApiEndpoint(`/patient-profiles/${patientId}`),
        {
          method: "GET",
          signal,
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch patient profile: ${response.statusText}`,
        );
      }

      return response.json() as Promise<D1NAMOPatientProfile>;
    },
    enabled: Boolean(patientId),
    staleTime: 15 * 60 * 1000, // 15 minutes - profile data changes infrequently
  });
}

/**
 * Hook for fetching patients with D1NAMO data counts
 */
export function usePatientsWithD1NAMOData(options?: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["patients-with-d1namo-data", options],
    queryFn: async ({ signal }) => {
      const queryParams = new URLSearchParams();
      if (options?.page) queryParams.append("page", options.page.toString());
      if (options?.limit) queryParams.append("limit", options.limit.toString());

      const endpoint = buildApiEndpoint(
        "/patients/d1namo",
        Object.fromEntries(queryParams.entries()),
      );

      const response = await authenticatedRequest(endpoint, {
        method: "GET",
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch patients with D1NAMO data: ${response.statusText}`,
        );
      }

      const data = (await response.json()) as {
        success: boolean;
        searchResults: PatientSearchResponse;
        metadata?: {
          recordCount: number;
          queryTime: number;
        };
      };

      return data;
    },
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      // Sort patients by data completeness
      const sortedPatients = data.searchResults.patients.sort((a, b) => {
        const aScore =
          (a.glucoseReadings || 0) +
          (a.ecgReadings || 0) +
          (a.insulinMeasurements || 0) +
          (a.mealImages || 0);
        const bScore =
          (b.glucoseReadings || 0) +
          (b.ecgReadings || 0) +
          (b.insulinMeasurements || 0) +
          (b.mealImages || 0);
        return bScore - aScore;
      });

      return {
        ...data,
        searchResults: {
          ...data.searchResults,
          patients: sortedPatients,
        },
      };
    },
  });
}

/**
 * Hook for advanced patient search with comprehensive filtering
 */
export function usePatientSearch(searchRequest: PatientSearchRequest) {
  return useQuery({
    queryKey: ["patient-search", searchRequest],
    queryFn: async ({ signal }) => {
      const queryParams = new URLSearchParams();

      // Add all search parameters
      Object.entries(searchRequest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value.toString());
        }
      });

      const endpoint = buildApiEndpoint(
        "/patients/search",
        Object.fromEntries(queryParams.entries()),
      );

      const response = await authenticatedRequest(endpoint, {
        method: "GET",
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to search patients: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        success: boolean;
        searchResults: PatientSearchResponse;
        metadata?: {
          recordCount: number;
          queryTime: number;
          performanceMetrics?: {
            queryExecutionTime: number;
            dataProcessingTime: number;
            totalResponseTime: number;
          };
        };
      };

      return data;
    },
    staleTime: 3 * 60 * 1000, // Shorter cache for search results
    enabled: Object.keys(searchRequest).length > 0, // Only run if there are search parameters
  });
}
